import { Heading } from '@components/rolex-ui/Heading'
import SliderNavButton from './SliderNavButton'
import { Image, Track, breakpoints, useSlider } from '@gassan-ui'
import { useBreakpoint } from '@lib/hooks/useBreakpoint'
import { getKontentImage } from '@lib/kontent'
import { RolexKeepExploringSliderItem } from '@lib/kontent/models/RolexKeepExploringSliderItem'
import _chunk from 'lodash.chunk'
import { useTranslation } from 'next-i18next'
import { FC, Fragment, useRef } from 'react'
import { Text } from '@components/rolex-ui/Text'
import { RolexContainer } from '@components/_rolex/Container'
import { cn } from '@lib/cn'
import Indicators from '@components/_rolex/sliders/Indicators'

type Props = {
  items: RolexKeepExploringSliderItem[]
  title: string
}

const KeepExploring: FC<Props> = ({ items, title }) => {
  const {
    i18n: { language },
  } = useTranslation()
  const isTabletUp = useBreakpoint(breakpoints.small)
  const isDesktopUp = useBreakpoint(breakpoints.large)
  const chunks = _chunk(items, isDesktopUp ? 4 : isTabletUp ? 3 : 2)
  const trackRef = useRef<HTMLDivElement>(null)
  const { onTouchEnd, prev, next, canMoveRight, canMoveLeft, index, goToIndex } = useSlider({
    trackRef,
  })

  return (
    <div className="overflow-hidden">
      <RolexContainer className={cn('mb-[60px]', 'lg:mb-[90px]')}>
        <Heading variant="headline36" className="mb-5" as="h2">
          {title}
        </Heading>
        <div className="relative mb-8 md:mb-9 lg:mb-10">
          <Track ref={trackRef} onTouchEnd={onTouchEnd} className="gap-x-5">
            {chunks.map((chunk, key) => (
              <Fragment key={key}>
                <div className="grid min-w-full snap-start grid-cols-2 gap-x-4 sm:grid-cols-3 lg:grid-cols-4">
                  {chunk.map((item, key) => (
                    <a
                      className="group w-full"
                      href={`${language !== 'nl' ? `/${language}` : ''}${item.url.value}`}
                      key={key}
                    >
                      <Image
                        className="aspect-[10/11]"
                        alt={item.image.value[0].description}
                        sources={[
                          getKontentImage(item.image.value[0].url, { width: 300 }),
                          getKontentImage(item.image.value[0].url, { width: 500 }),
                        ]}
                        placeholder={getKontentImage(item.image.value[0].url, { width: 30 })}
                      />
                      <div className="relative z-[2] bg-white pt-[10px] transition-transform group-hover:-translate-y-5">
                        <Text variant="body20">{item.title.value}</Text>
                      </div>
                    </a>
                  ))}
                </div>
              </Fragment>
            ))}
          </Track>
          {chunks.length > 1 && isTabletUp && (
            <div
              className={cn(
                'pointer-events-none absolute -left-[70px] top-1/2 z-[2] flex w-[calc(100%+140px)] -translate-y-1/2 justify-between',
                'md:-left-[60px] md:w-[calc(100%+120px)]',
                'lg:-left-[70px] lg:w-[calc(100%+140px)]',
              )}
            >
              <SliderNavButton direction="prev" onClick={prev} disabled={!canMoveLeft} />
              <SliderNavButton direction="next" onClick={next} disabled={!canMoveRight} />
            </div>
          )}
        </div>
        <Indicators
          activeIndex={index}
          variant="animated"
          count={chunks.length}
          onChangeIndex={goToIndex}
        />
      </RolexContainer>
    </div>
  )
}

export default KeepExploring
